<script lang="ts">
  /**
   * A date field that belongs to this interface.
   *
   * `<input type="date">` was doing the job and looked like a different application: the
   * browser's own widget, its own colours, its own calendar popup with a light background in a
   * dark page, and a control that ignores every token the rest of the page is built from.
   *
   * So: a real Svelte control. Deliberately not a dependency — a date picker is a month grid,
   * some arithmetic and a keyboard handler, and taking on a library for it would be the largest
   * runtime dependency in the front end for the smallest reason.
   *
   * `value` is `YYYY-MM-DD` and stays that way, because that is what the API takes. Everything
   * the user sees is `DD.MM.YYYY`.
   *
   * **UTC throughout.** `new Date('2026-08-21')` parses as UTC while `new Date(2026, 7, 21)`
   * is local, and mixing the two shifts a date by a day for anyone east or west of Greenwich —
   * the classic way an "until today" filter quietly loses today.
   */
  interface Props {
    /** `YYYY-MM-DD`. Bindable. */
    value: string;
    label: string;
    /** `YYYY-MM-DD`, inclusive. */
    min?: string | undefined;
    max?: string | undefined;
  }

  let { value = $bindable(), label, min, max }: Props = $props();

  const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  /**
   * Today, in UTC, read once when the component is created.
   *
   * UTC to match everything else in here — marking "today" from a local clock would put the dot
   * on the wrong cell for part of the day, which is the same off-by-one this file's header
   * warns about.
   */
  const TODAY = new Date().toISOString().slice(0, 10);
  const MONTHS = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];

  let open = $state(false);
  let root: HTMLDivElement;

  const parse = (iso: string): Date => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
  };
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  const pretty = (v: string): string => {
    const d = parse(v);
    return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
  };

  // The month on show, which follows `value` until the user pages away from it.
  let cursor = $state(parse(value));
  $effect(() => {
    if (!open) cursor = parse(value);
  });

  const disabled = (d: Date): boolean => {
    const s = iso(d);
    return (min !== undefined && s < min) || (max !== undefined && s > max);
  };

  /**
   * The visible grid: six rows of seven, Monday first.
   *
   * Monday first because this is a German interface, and `getUTCDay()` returns 0 for Sunday —
   * the `(day + 6) % 7` is what turns that into a Monday-based column.
   */
  const days = $derived.by(() => {
    const firstOfMonth = new Date(
      Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1),
    );
    const lead = (firstOfMonth.getUTCDay() + 6) % 7;
    const start = new Date(firstOfMonth);
    start.setUTCDate(1 - lead);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setUTCDate(start.getUTCDate() + i);
      return d;
    });
  });

  function shiftMonth(by: number): void {
    const next = new Date(cursor);
    next.setUTCMonth(cursor.getUTCMonth() + by, 1);
    cursor = next;
  }

  function pick(d: Date): void {
    if (disabled(d)) return;
    value = iso(d);
    open = false;
  }

  // Click-away and Escape. Without both, a popup over a map is a trap: the click that should
  // pan the map dismisses nothing and the keyboard cannot get out.
  $effect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root !== undefined && !root.contains(e.target as Node)) open = false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') open = false;
    };
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  });
</script>

<div class="picker" bind:this={root}>
  <button
    class="field"
    class:open
    onclick={() => (open = !open)}
    type="button"
    aria-haspopup="dialog"
    aria-expanded={open}
  >
    <span class="label">{label}</span>
    <span class="value">{pretty(value)}</span>
  </button>

  {#if open}
    <div class="pop" role="dialog" aria-label="{label} wählen">
      <div class="nav">
        <button onclick={() => shiftMonth(-1)} type="button" aria-label="Vorheriger Monat">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <span class="month">{MONTHS[cursor.getUTCMonth()]} {cursor.getUTCFullYear()}</span>
        <button onclick={() => shiftMonth(1)} type="button" aria-label="Nächster Monat">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      <div class="grid">
        {#each WEEKDAYS as w, i (w)}
          <span class="wd" class:weekend={i > 4}>{w}</span>
        {/each}
        {#each days as d (d.getTime())}
          {@const s = iso(d)}
          <button
            class="day"
            class:other={d.getUTCMonth() !== cursor.getUTCMonth()}
            class:sel={s === value}
            class:today={s === TODAY}
            disabled={disabled(d)}
            onclick={() => pick(d)}
            type="button"
            aria-current={s === value ? 'date' : undefined}>{d.getUTCDate()}</button
          >
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .picker {
    position: relative;
  }

  /*
    Centred, not baseline-aligned. The label and the date are set at different sizes, so a
    shared baseline puts the pair low in the pill and the whole control sits visibly below the
    chips beside it — which is exactly how it looked.
  */
  .field {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border: 0;
    background: transparent;
    font: inherit;
    /* Matches `.group button` in the bars this sits in, so the pill keeps one height. */
    font-size: 0.85rem;
    line-height: 1.25;
    color: var(--text);
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    cursor: pointer;
    white-space: nowrap;
    transition: background 120ms ease;
  }
  .field:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  .field.open {
    background: rgba(255, 255, 255, 0.1);
  }
  .field:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  .label {
    font-size: 0.72rem;
    letter-spacing: 0.02em;
    color: var(--text-dim);
  }
  .value {
    font-variant-numeric: tabular-nums;
  }

  .pop {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    z-index: 40;
    /* Same recipe as the map controls: a translucent panel over a blur, so it reads as part of
       the interface rather than a window sitting on top of it. */
    background: color-mix(in srgb, var(--surface-2, #161a22) 88%, transparent);
    backdrop-filter: blur(14px);
    border: 1px solid var(--line-strong);
    border-radius: 0.9rem;
    padding: 0.7rem 0.7rem 0.55rem;
    box-shadow:
      0 18px 44px rgba(0, 0, 0, 0.5),
      0 1px 0 rgba(255, 255, 255, 0.04) inset;
    width: 17.5rem;
  }

  .nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.55rem;
  }
  .month {
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }
  .nav button {
    display: grid;
    place-items: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 0;
    background: transparent;
    color: var(--text-dim);
    border-radius: 999px;
    cursor: pointer;
    transition:
      background 120ms ease,
      color 120ms ease;
  }
  .nav button svg {
    width: 1rem;
    height: 1rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .nav button:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
  }

  .wd {
    text-align: center;
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--text-dim);
    padding-bottom: 0.35rem;
  }
  .wd.weekend {
    opacity: 0.55;
  }

  .day {
    position: relative;
    display: grid;
    place-items: center;
    border: 0;
    background: transparent;
    color: var(--text);
    font: inherit;
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
    aspect-ratio: 1;
    /* A circle, not a rounded square: the selected day then matches the pills everywhere else
       instead of being the one hard-cornered element in the interface. */
    border-radius: 999px;
    cursor: pointer;
    transition:
      background 100ms ease,
      color 100ms ease;
  }
  .day:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.09);
  }
  .day:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
  .day.other {
    color: var(--text-dim);
    opacity: 0.4;
  }

  /* Today is marked with a dot rather than a border, so it stays legible under the selection
     ring instead of fighting it. */
  .day.today::after {
    content: '';
    position: absolute;
    bottom: 0.18rem;
    width: 3px;
    height: 3px;
    border-radius: 999px;
    background: var(--accent);
  }
  .day.sel {
    background: var(--accent);
    color: #04121a;
    font-weight: 700;
  }
  .day.sel.today::after {
    background: #04121a;
  }
  .day:disabled {
    opacity: 0.18;
    cursor: not-allowed;
  }
</style>
